import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Card, InputAdornment, LinearProgress, Pagination, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import {
  Add as AddIcon, ChecklistRounded as ChecklistIcon, UploadFileOutlined as UploadIcon,
  Search as SearchIcon, Download as DownloadIcon, LibraryAddOutlined as BulkIcon,
} from '@mui/icons-material';
import DocumentService, {
  ComplianceBoard, DocumentOwnerType, EntityFileRow, FileFolder, FolderOwner, TopOpsDocument,
} from '../../api/services/document.service';
import ComplianceStrip from '../../components/files/ComplianceStrip';
import BulkUploadDialog from '../../components/files/BulkUploadDialog';
import { downloadBlob } from '../../utils/download';
import FilesShell, { FileDocumentButton, useFileUpload } from '../../components/files/FilesShell';
import PageHeader from '../../components/ui/PageHeader';
import { EmptyState, LoadingState } from '../../components/ui/States';
import { usePageBreadcrumb } from '../../components/layout/Breadcrumb';
import FileTable from '../../components/files/FileTable';
import DocumentInspector from '../../components/files/DocumentInspector';
import AttentionLine, { Attention, attentionTotal } from '../../components/files/AttentionLine';
import {
  FileGrid, FolderItems, ViewModeToggle, useViewMode,
} from '../../components/files/FolderBrowser';
import { childrenOf, pathTo, useFileFolders } from '../../components/files/useFileFolders';

// One folder: the folders inside it, then the files filed directly in it.
//
// Nothing here knows what the folders are. The tree is the server's
// (GET /api/documents/folders), so Company › Payroll › FedEx Statements is
// a row of data, and regrouping Files is a change there, not here.
//
// A folder with an ownerType holds one folder per owner: Drivers & Staff
// lists its people, Vehicles its trucks, Accidents its cases. Opening one
// (?owner=) shows the same subfolders, counted and filled for that owner —
// Drivers & Staff › Dana Reyes › License & Medical.
//
// What is missing or running out is not a separate screen. It rides on the
// tree: every folder, person and truck says what needs doing beneath it; a
// required document nobody has filed sits in its folder as an empty slot
// you can upload straight into; and "Needs attention" (?view=attention)
// narrows the same tree to only what needs doing, wherever you are in it.
//
// The top of the tree is this page too (/files, no folder key): Files opens
// on its folders the way Drive opens on My Drive, and going in and out of a
// folder changes the folder, not the screen. A folder's contents stay up
// while the next one loads, under a thin bar, rather than blinking to a
// spinner on every click.

const PAGE_SIZE = 25;

type Owner = { ownerType: DocumentOwnerType; ownerId: string; label: string };

/** Files straight into this folder — its type chosen when there is one, its owner always. */
const AddFileButton: React.FC<{ typeKeys: string[]; owner?: Owner }> = ({ typeKeys, owner }) => {
  const openUpload = useFileUpload();
  if (!typeKeys.length && !owner) return <FileDocumentButton />;
  return (
    <Button
      variant="contained" disableElevation startIcon={<AddIcon />}
      onClick={() => openUpload({
        ...(typeKeys.length === 1 && { typeKey: typeKeys[0] }),
        ...(owner && { owner }),
      })}
    >
      Add file
    </Button>
  );
};

/**
 * A required document with nothing on file, in the folder it belongs in —
 * the checklist row and the folder are one place. Clicking files it there,
 * for this person, as that type.
 */
const EmptySlotRow: React.FC<{ row: EntityFileRow; owner?: Owner; first: boolean }> = ({ row, owner, first }) => {
  const openUpload = useFileUpload();
  const upload = () => openUpload({ typeKey: row.documentTypeKey, ...(owner && { owner }) });
  const word = row.status === 'overdue' || row.status === 'lapsed' ? 'Expired' : 'Missing';
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={upload}
      onKeyDown={(e) => { if (e.key === 'Enter') upload(); }}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, cursor: 'pointer', minHeight: 56,
        borderTop: first ? 'none' : '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box sx={{ width: 28, height: 28, borderRadius: '7px', border: '1.5px dashed', borderColor: 'error.main', opacity: 0.6, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{row.label}</Typography>
        <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600 }}>{word} · nothing on file</Typography>
      </Box>
      <Button
        size="small" variant="outlined" startIcon={<UploadIcon />}
        onClick={(e) => { e.stopPropagation(); upload(); }} sx={{ flexShrink: 0 }}
      >
        Upload
      </Button>
    </Box>
  );
};

const FolderPage: React.FC = () => {
  const navigate = useNavigate();
  const { folderKey = '' } = useParams<{ folderKey: string }>();
  const [params] = useSearchParams();
  const ownerId = params.get('owner') || '';
  const attentionView = params.get('view') === 'attention';
  const { folders: companyFolders, loaded, reload } = useFileFolders();
  const [mode, setMode] = useViewMode();
  const isRoot = !folderKey;

  // The top of the tree carries what the old Files home did: search, the
  // compliance line and what was filed lately.
  const [board, setBoard] = useState<ComplianceBoard | null>(null);
  const [recent, setRecent] = useState<TopOpsDocument[]>([]);
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);
  const loadRoot = useCallback(() => {
    if (!isRoot) return;
    // A failed load says so, rather than showing an empty, all-clear vault.
    DocumentService.getBoard().then(setBoard)
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load the vault'));
    DocumentService.list({ pageSize: 15 }).then((r) => setRecent(r.items || []))
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load the vault'));
  }, [isRoot]);
  useEffect(() => { loadRoot(); }, [loadRoot]);
  const [bulkOpen, setBulkOpen] = useState(false);

  // Inside one owner's folder the counts are theirs, so the tree is fetched
  // for them; everywhere else the shared, company-wide tree is used.
  const [ownerFolders, setOwnerFolders] = useState<FileFolder[] | null>(null);
  const loadOwnerFolders = useCallback(() => {
    if (!ownerId) { setOwnerFolders(null); return; }
    DocumentService.getFolders(ownerId).then(setOwnerFolders).catch(() => setOwnerFolders([]));
  }, [ownerId]);
  useEffect(() => { loadOwnerFolders(); }, [loadOwnerFolders]);
  const folders = ownerId && ownerFolders ? ownerFolders : companyFolders;

  const folder = isRoot ? null : folders.find((f) => f.key === folderKey) || null;
  // Memoised: a fresh array every render would re-run everything keyed on
  // it, and the checklist fetch below would loop.
  const trail = useMemo(() => pathTo(folders, folderKey), [folders, folderKey]);
  // The folder that holds one folder per owner: Drivers & Staff for
  // anything beneath it.
  const ownerRoot = useMemo(() => trail.find((f) => f.ownerType) || null, [trail]);
  const listsOwners = !!ownerRoot && ownerRoot.key === folderKey && !ownerId;
  const atOwner = !!ownerRoot && ownerRoot.key === folderKey && !!ownerId;

  // The people / trucks / cases — listed at the root, and needed inside one
  // for its name and what it needs.
  const [owners, setOwners] = useState<FolderOwner[] | null>(null);
  const loadOwners = useCallback(() => {
    if (!ownerRoot) { setOwners(null); return; }
    DocumentService.getFolderOwners(ownerRoot.key, true).then(setOwners).catch(() => setOwners([]));
  }, [ownerRoot]);
  useEffect(() => { loadOwners(); }, [loadOwners]);
  const owner = ownerId ? owners?.find((o) => o.ownerId === ownerId) || null : null;
  const uploadOwner: Owner | undefined = ownerId && ownerRoot?.ownerType && owner
    ? { ownerType: ownerRoot.ownerType, ownerId, label: owner.label }
    : undefined;

  // Whose checklist this folder's empty slots come from: the owner's, or —
  // under Company — the company's.
  // Keyed on strings, so it changes only when the owner does.
  const slotType: DocumentOwnerType | null = ownerRoot?.ownerType && ownerId
    ? ownerRoot.ownerType
    : !ownerRoot && trail[0]?.key === 'company' ? 'company' : null;
  const slotId = slotType === 'company' ? 'company' : ownerId;
  const fileHere = (folder?.typeKeys.length || 0) > 0;
  const [checklist, setChecklist] = useState<EntityFileRow[]>([]);
  const loadChecklist = useCallback(() => {
    if (!slotType || !fileHere) { setChecklist([]); return; }
    DocumentService.getEntityFile(slotType, slotId)
      .then((file) => setChecklist(file.rows || []))
      .catch(() => setChecklist([]));
  }, [slotType, slotId, fileHere]);
  useEffect(() => { loadChecklist(); }, [loadChecklist]);
  const emptySlots = useMemo(() => {
    const here = new Set(folder?.typeKeys || []);
    return checklist.filter((r) => here.has(r.documentTypeKey) && r.requirement !== 'optional' && !r.hasDocument);
  }, [checklist, folder]);

  const [items, setItems] = useState<TopOpsDocument[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<TopOpsDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Files filed directly here — for an owner's folder, theirs. Nothing at a
  // per-owner root: there the rows are people, not files.
  const hasFiles = !!folder && folder.typeKeys.length > 0 && !listsOwners && (!ownerRoot || !!ownerId);
  const load = useCallback(async () => {
    if (!hasFiles) { setItems([]); setTotal(0); setLoading(false); return; }
    // The current list stays up under a thin bar while this one loads.
    setLoading(true);
    try {
      const base = { folder: folderKey, ...(ownerId && { ownerId }) };
      if (attentionView) {
        // Only what is running out or has: the two statuses, together.
        const [expired, expiring] = await Promise.all([
          DocumentService.list({ ...base, status: 'expired', pageSize: 100 }),
          DocumentService.list({ ...base, status: 'expiring_soon', pageSize: 100 }),
        ]);
        const both = [...(expired.items || []), ...(expiring.items || [])];
        setItems(both);
        setTotal(both.length);
      } else {
        const result = await DocumentService.list({ ...base, page, pageSize: PAGE_SIZE });
        setItems(result.items || []);
        setTotal(result.total || 0);
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load this folder');
    } finally {
      setLoading(false);
    }
  }, [folderKey, ownerId, page, hasFiles, attentionView]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); setSelected(null); }, [folderKey, ownerId, attentionView]);

  // Links keep the owner and the lens, so Dana's License & Medical stays
  // Dana's, and "needs attention" stays on as you go down.
  const href = (key: string, who: string = ownerId, view: boolean = attentionView) => {
    const query = new URLSearchParams();
    if (who) query.set('owner', who);
    if (view) query.set('view', 'attention');
    const q = query.toString();
    return `${key ? `/files/folder/${key}` : '/files'}${q ? `?${q}` : ''}`;
  };

  // Drivers & Staff › Dana Reyes › License & Medical
  const crumbs = useMemo(() => {
    const out: { label: string; to?: string }[] = [];
    trail.forEach((f) => {
      out.push({ label: f.name, to: href(f.key, '') });
      if (ownerRoot && f.key === ownerRoot.key && ownerId) out.push({ label: owner?.label || '…', to: href(f.key) });
    });
    if (out.length) delete out[out.length - 1].to;
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trail, ownerRoot, ownerId, owner, attentionView]);
  usePageBreadcrumb(isRoot ? [] : [{ label: 'Files', to: '/files' }, ...crumbs]);

  // What needs doing here: the owner's when inside one, else the folder's.
  const here: Attention | undefined = isRoot
    ? childrenOf(folders, null).reduce<Attention>((sum, f) => ({
      missing: sum.missing + (f.attention?.missing || 0),
      expiring: sum.expiring + (f.attention?.expiring || 0),
      expired: sum.expired + (f.attention?.expired || 0),
    }), { missing: 0, expiring: 0, expired: 0 })
    : atOwner ? owner?.attention : folder?.attention;
  const needs = attentionTotal(here);

  const shownOwners = (owners || []).filter((o) => !attentionView || attentionTotal(o.attention) > 0);
  const subfolders = listsOwners ? [] : childrenOf(folders, isRoot ? null : folderKey)
    .filter((f) => !attentionView || attentionTotal(f.attention) > 0);
  const title = isRoot ? 'Files' : atOwner ? owner?.label || '' : folder?.name || (loaded ? 'Folder not found' : '');
  const count = atOwner ? owner?.count ?? folder?.count ?? 0 : folder?.count ?? 0;
  // People and trucks can be filed for in bulk: every driver's I-9 at once.
  const bulkFor = listsOwners && (ownerRoot?.ownerType === 'driver' || ownerRoot?.ownerType === 'vehicle') ? ownerRoot : null;
  // A person's or a truck's required-paperwork checklist.
  const checklistLink = ownerId && (ownerRoot?.ownerType === 'driver' || ownerRoot?.ownerType === 'vehicle')
    ? `/files/entity/${ownerRoot.ownerType}/${ownerId}`
    : folderKey === 'company' ? '/files/entity/company/company' : null;
  const nothingToDo = attentionView && !needs && !emptySlots.length;

  return (
    <FilesShell
      onUploaded={() => { load(); reload(); loadOwnerFolders(); loadOwners(); loadChecklist(); loadRoot(); }}
      // Always there, so every folder is the same width — a folder of
      // folders and a folder of files no longer differ by a side panel.
      inspector={<DocumentInspector document={selected} />}
      header={(
        <PageHeader
          title={title}
          subtitle={isRoot ? 'Every document the company has to be able to produce.' : folder ? `${count} ${count === 1 ? 'file' : 'files'}` : undefined}
          actions={isRoot ? (
            <>
              <Button
                variant="outlined" startIcon={<DownloadIcon />} disabled={exporting}
                onClick={async () => {
                  setExporting(true);
                  try {
                    const blob = await DocumentService.exportZip({ scope: 'company', category: 'all' });
                    downloadBlob(blob, `toposds-documents-${new Date().toISOString().slice(0, 10)}.zip`);
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'Could not build that export');
                  } finally { setExporting(false); }
                }}
              >
                {exporting ? 'Preparing…' : 'Export all'}
              </Button>
              <FileDocumentButton />
            </>
          ) : folder && (
            <>
              {bulkFor && (
                <Button variant="outlined" startIcon={<BulkIcon />} onClick={() => setBulkOpen(true)}>
                  File many
                </Button>
              )}
              {checklistLink && (
                <Button variant="outlined" startIcon={<ChecklistIcon />} onClick={() => navigate(checklistLink)}>
                  Checklist
                </Button>
              )}
              {!listsOwners && (!ownerRoot || uploadOwner) && (
                <AddFileButton typeKeys={folder.typeKeys} owner={uploadOwner} />
              )}
            </>
          )}
        >
          {isRoot && (
            <Box
              component="form" sx={{ maxWidth: 560 }}
              onSubmit={(e: React.FormEvent) => {
                e.preventDefault();
                if (search.trim()) navigate(`/files/search?q=${encodeURIComponent(search.trim())}`);
              }}
            >
              <TextField
                fullWidth placeholder="Search files, people or vehicles…" value={search} onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} /></InputAdornment> }}
              />
            </Box>
          )}
        </PageHeader>
      )}
    >
      {bulkFor && bulkFor.ownerType && (
        <BulkUploadDialog
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          onUploaded={() => { loadOwners(); reload(); }}
          ownerType={bulkFor.ownerType}
          ownerFolderKey={bulkFor.key}
        />
      )}
      {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}

      {loaded && !folder && !isRoot ? (
        <EmptyState title="Folder not found" description="It may have been moved, or you may not have access to it." />
      ) : (
        <Stack gap={2} sx={{ mt: 2 }}>
          {isRoot && <ComplianceStrip board={board} onSegmentClick={() => navigate('/files/compliance')} />}
          {/* The same tree, narrowed to what needs doing. */}
          <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
            <ToggleButtonGroup
              size="small" exclusive value={attentionView ? 'attention' : 'all'}
              onChange={(_, v) => v && navigate(href(folderKey, ownerId, v === 'attention'), { replace: true })}
            >
              <ToggleButton value="all" sx={{ px: 2 }}>All files</ToggleButton>
              <ToggleButton value="attention" sx={{ px: 2 }}>
                Needs attention{needs ? ` (${needs})` : ''}
              </ToggleButton>
            </ToggleButtonGroup>
            <AttentionLine attention={here} />
            <Box sx={{ ml: 'auto' }}><ViewModeToggle mode={mode} onChange={setMode} /></Box>
          </Stack>

          {nothingToDo && (
            <EmptyState title="Nothing needs attention here" description="Nothing missing, nothing running out." />
          )}

          {listsOwners && owners === null && <LoadingState label="Loading…" />}
          <FolderItems
            mode={mode}
            items={listsOwners
              ? shownOwners.map((o) => ({
                key: o.ownerId,
                name: o.label,
                sublabel: [o.sublabel, o.inactive ? 'Inactive' : null].filter(Boolean).join(' · ') || null,
                count: o.count,
                attention: o.attention,
                ownerType: o.ownerType,
                muted: o.inactive,
                onOpen: () => navigate(href(folderKey, o.ownerId)),
              }))
              : subfolders.map((sub) => ({
                key: sub.key,
                name: sub.name,
                count: sub.count,
                attention: sub.attention,
                onOpen: () => navigate(href(sub.key)),
              }))}
          />

          {emptySlots.length > 0 && (
            <Card sx={{ overflow: 'hidden', borderColor: 'error.light' }}>
              {emptySlots.map((row, index) => (
                <EmptySlotRow key={row.documentTypeKey} row={row} owner={uploadOwner} first={index === 0} />
              ))}
            </Card>
          )}

          {hasFiles && (loading && !items.length ? (
            <LoadingState label="Loading files…" />
          ) : (!attentionView || items.length > 0) && (
            <>
              {loading && <LinearProgress sx={{ height: 2, borderRadius: 1 }} />}
              {mode === 'grid' && items.length > 0 ? (
                <FileGrid
                  documents={items}
                  selectedId={selected?._id}
                  onSelect={setSelected}
                  onOpen={(document) => navigate(`/files/document/${document._id}`)}
                />
              ) : (
                <FileTable
                  documents={items}
                  selectedId={selected?._id}
                  onSelect={setSelected}
                  onOpen={(document) => navigate(`/files/document/${document._id}`)}
                  emptyMessage={emptySlots.length ? 'Nothing filed yet — the slots above are what is required.' : 'Nothing filed here yet.'}
                />
              )}
              {!attentionView && total > PAGE_SIZE && (
                <Pagination
                  count={Math.ceil(total / PAGE_SIZE)} page={page} onChange={(_, value) => setPage(value)}
                  sx={{ display: 'flex', justifyContent: 'center' }}
                />
              )}
            </>
          ))}
          {isRoot && (
            <>
              <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mt: 2 }}>
                <Typography variant="overline" color="text.disabled">Recently filed</Typography>
                <Typography variant="caption" color="text.disabled">Click a row to inspect · double-click to open</Typography>
              </Stack>
              <FileTable
                documents={recent}
                showCategory
                selectedId={selected?._id}
                onSelect={setSelected}
                onOpen={(document) => navigate(`/files/document/${document._id}`)}
                emptyMessage="No documents filed yet. Use “File a document” to add the first one."
              />
            </>
          )}
        </Stack>
      )}
    </FilesShell>
  );
};

export default FolderPage;
